import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useServiceOS } from '../hooks/useServiceOS';
import {
  Users,
  CalendarCheck,
  Clock,
  EnvelopeSimple,
  ArrowRight,
  TrendUp,
  Warning,
  Copy,
  CheckCircle,
} from '@phosphor-icons/react';

function StatCard({ icon: Icon, label, value, color, sub, testId }) {
  return (
    <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} weight="duotone" />
        </div>
        <span className="text-sm text-[#8B8FA3]">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-[#8B8FA3] mt-1">{sub}</p>}
    </div>
  );
}

export default function SOSDashboard() {
  const { getDashboard } = useServiceOS();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboxCopied, setInboxCopied] = useState(false);

  useEffect(() => {
    getDashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, [getDashboard]);

  const copyInbox = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = email;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setInboxCopied(true);
    setTimeout(() => setInboxCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sos-dashboard-loading">
        <div className="w-6 h-6 border-2 border-[#14F195] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { leads, jobs, followups, replies, recent_activity, profile, sources } = data;

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="sos-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{profile.business_name}</h1>
        <p className="text-sm text-[#8B8FA3] mt-1 capitalize">{profile.business_type}</p>
      </div>

      {/* Alert banner */}
      {(leads.new > 0 || followups.due > 0) && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl" data-testid="sos-dashboard-alert">
          <Warning size={18} className="text-[#F59E0B]" weight="fill" />
          <span className="text-sm text-[#F59E0B]">
            {leads.new > 0 && `${leads.new} new lead${leads.new > 1 ? 's' : ''} need attention`}
            {leads.new > 0 && followups.due > 0 && ' · '}
            {followups.due > 0 && `${followups.due} follow-up${followups.due > 1 ? 's' : ''} overdue`}
          </span>
          <Link to="/service-os/leads?status=new" className="ml-auto text-xs font-medium text-[#F59E0B] hover:underline" data-testid="sos-dashboard-alert-link">
            View <ArrowRight size={12} className="inline" />
          </Link>
        </div>
      )}

      {/* Inbox email */}
      {profile?.lead_inbox_email && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#14F195]/5 border border-[#14F195]/20 rounded-xl" data-testid="sos-inbox-email-card">
          <EnvelopeSimple size={16} className="text-[#14F195] shrink-0" weight="duotone" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#8B8FA3]">Forward enquiries to</p>
            <code className="text-sm text-[#14F195] font-mono truncate block" data-testid="dashboard-inbox-email">{profile.lead_inbox_email}</code>
          </div>
          <button
            onClick={() => copyInbox(profile.lead_inbox_email)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 transition-colors ${
              inboxCopied
                ? 'bg-[#22C55E]/10 text-[#22C55E]'
                : 'bg-[#14F195]/10 text-[#14F195] hover:bg-[#14F195]/20'
            }`}
            data-testid="dashboard-copy-inbox-btn"
          >
            {inboxCopied ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="New leads" value={leads.new} color="bg-[#14F195]/10 text-[#14F195]" sub={`${leads.recent_7d} this week`} testId="stat-new-leads" />
        <StatCard icon={CalendarCheck} label="Jobs today" value={jobs.today} color="bg-[#6366F1]/10 text-[#6366F1]" sub={`${jobs.completed} completed total`} testId="stat-jobs-today" />
        <StatCard icon={Clock} label="Follow-ups due" value={followups.due} color="bg-[#F59E0B]/10 text-[#F59E0B]" sub={`${followups.upcoming} upcoming`} testId="stat-followups" />
        <StatCard icon={EnvelopeSimple} label="Draft replies" value={replies.pending} color="bg-[#EC4899]/10 text-[#EC4899]" sub="Ready to review" testId="stat-replies" />
      </div>

      {/* Lead pipeline */}
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="sos-pipeline">
        <h2 className="text-base font-semibold text-white mb-4">Lead pipeline</h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[
            { label: 'New', count: leads.new, color: 'bg-[#14F195]' },
            { label: 'Contacted', count: leads.contacted, color: 'bg-[#6366F1]' },
            { label: 'Awaiting reply', count: leads.awaiting_reply, color: 'bg-[#F59E0B]' },
            { label: 'Booked', count: leads.booked, color: 'bg-[#22C55E]' },
            { label: 'Lost', count: leads.lost, color: 'bg-[#EF4444]' },
          ].map(stage => (
            <div key={stage.label} className="flex-1 min-w-[100px] bg-[#0B0C10] rounded-lg p-3 text-center">
              <div className={`w-2 h-2 rounded-full ${stage.color} mx-auto mb-2`} />
              <p className="text-lg font-bold text-white">{stage.count}</p>
              <p className="text-xs text-[#8B8FA3]">{stage.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Lead sources */}
      {sources && (sources.email > 0 || sources.manual > 0 || sources.website > 0 || sources.test > 0) && (
        <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="sos-lead-sources">
          <h2 className="text-base font-semibold text-white mb-4">Lead sources</h2>
          <div className="flex gap-3 overflow-x-auto">
            {[
              { label: 'Email', count: sources.email, color: 'bg-[#3B82F6]', text: 'text-[#3B82F6]' },
              { label: 'Manual', count: sources.manual, color: 'bg-[#8B5CF6]', text: 'text-[#8B5CF6]' },
              { label: 'Website', count: sources.website, color: 'bg-[#F97316]', text: 'text-[#F97316]' },
            ].filter(s => s.count > 0).map(s => (
              <div key={s.label} className="flex items-center gap-3 px-4 py-3 bg-[#0B0C10] rounded-lg min-w-[140px]" data-testid={`source-${s.label.toLowerCase()}`}>
                <div className={`w-2 h-2 rounded-full ${s.color} shrink-0`} />
                <div>
                  <p className={`text-lg font-bold ${s.text}`}>{s.count}</p>
                  <p className="text-xs text-[#8B8FA3]">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="sos-recent-activity">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Recent activity</h2>
          <Link to="/service-os/leads" className="text-xs text-[#14F195] hover:underline">View all leads</Link>
        </div>
        {recent_activity.length === 0 ? (
          <p className="text-sm text-[#8B8FA3]">No activity yet. Create your first lead to get started.</p>
        ) : (
          <div className="space-y-3">
            {recent_activity.slice(0, 8).map((a, i) => (
              <div key={a.id || i} className="flex items-start gap-3 py-2 border-b border-[#1E1F2E] last:border-0">
                <div className="w-2 h-2 rounded-full bg-[#6366F1] mt-2 shrink-0" />
                <div>
                  <p className="text-sm text-white">{a.title}</p>
                  {a.description && <p className="text-xs text-[#8B8FA3]">{a.description}</p>}
                  <p className="text-xs text-[#555] mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/service-os/leads"
          className="flex items-center justify-between px-5 py-4 bg-[#14F195]/5 border border-[#14F195]/20 rounded-xl hover:bg-[#14F195]/10 transition-colors group"
          data-testid="sos-quick-leads"
        >
          <div>
            <p className="text-sm font-semibold text-[#14F195]">Manage leads</p>
            <p className="text-xs text-[#8B8FA3] mt-0.5">{leads.total} total leads</p>
          </div>
          <ArrowRight size={16} className="text-[#14F195] group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/service-os/jobs"
          className="flex items-center justify-between px-5 py-4 bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-xl hover:bg-[#6366F1]/10 transition-colors group"
          data-testid="sos-quick-jobs"
        >
          <div>
            <p className="text-sm font-semibold text-[#6366F1]">View schedule</p>
            <p className="text-xs text-[#8B8FA3] mt-0.5">{jobs.total} total jobs</p>
          </div>
          <ArrowRight size={16} className="text-[#6366F1] group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}
